import styled from 'styled-components'

import { useOrderSummaryContext } from 'checkout/containers/OrderSummaryContextProvider'
import { t } from 'localization'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { CheckoutQA } from 'shared/dataAttributes'
import { PriceBreakdownType } from 'shared/enums/PriceBreakdown'
import { colors, fonts } from 'shared/lib'
import { displayPriceHideCentsCurrency } from 'shared/utils/currency-utils'

const TP = 'checkout.components.OrderSummaryPanel.OrderSummary'

const OrderSummaryPriceBreakDown = () => {
  const {
    availableCountries,
    country: shoppingCountryCode,
    currencyCode,
  } = useShoppingRegionContext()
  const {
    order: {
      credits,
      productPrice,
      promotion,
      priceBreakdownType,
      shippingAddress,
      shippingPrice,
      taxPrice,
      vatPrice,
    },
  } = useOrderSummaryContext()

  const shippingCountryCode = shippingAddress?.countryCode
  const isSubtotalOnly = priceBreakdownType === PriceBreakdownType.SUBTOTAL_ONLY
  const taxCopy =
    availableCountries.find(c => c.isoCode === (shippingCountryCode || shoppingCountryCode))
      ?.globalPricingShortCopy || ''

  return (
    <Container>
      <PriceLine>
        <DetailTitle data-qa={CheckoutQA.OrderSummarySubtotalTag}>
          {t(`${TP}.subTotal`, 'Subtotal')}
        </DetailTitle>
        <DetailValue data-qa={CheckoutQA.OrderSummarySubtotalAmount}>
          {displayPriceHideCentsCurrency(productPrice.display, currencyCode)}
        </DetailValue>
      </PriceLine>
      {promotion && (
        <PriceLine>
          <DetailTitle data-qa={CheckoutQA.OrderSummarySubtotalTag}>{promotion.title}</DetailTitle>
          <DetailValue data-qa={CheckoutQA.OrderSummarySubtotalAmount}>
            <Discount>-{promotion.value.display}</Discount>
          </DetailValue>
        </PriceLine>
      )}
      <PriceLine>
        <DetailTitle data-qa={CheckoutQA.OrderSummaryShippingTag}>
          {t(`${TP}.shipping`, 'Shipping')}
        </DetailTitle>
        <DetailValue data-qa={CheckoutQA.OrderSummaryShippingAmount}>
          {displayPriceHideCentsCurrency(shippingPrice.display, currencyCode)}
        </DetailValue>
      </PriceLine>
      <PriceLine>
        <DetailTitle data-qa={CheckoutQA.OrderSummaryTaxTag}>
          {isSubtotalOnly ? t(`${TP}.taxesAndDuties`, 'Taxes and Duties') : t(`${TP}.tax`, 'Tax')}
        </DetailTitle>
        <DetailValue data-qa={CheckoutQA.OrderSummaryTaxAmount}>
          {isSubtotalOnly ? `${taxCopy}` : taxPrice.display || vatPrice.display}
        </DetailValue>
      </PriceLine>
      {!!(credits && credits.value) && (
        <PriceLine>
          <DetailTitle data-qa={CheckoutQA.OrderSummaryCreditTag}>
            {t(`${TP}.credits`, 'Credits')}
          </DetailTitle>
          <DetailValue data-qa={CheckoutQA.OrderSummaryCreditAmount}>
            <Discount>-{credits.display}</Discount>
          </DetailValue>
        </PriceLine>
      )}
    </Container>
  )
}

export default OrderSummaryPriceBreakDown

const Container = styled.div`
  display: flex;
  flex-direction: column;
`
const DetailTitle = styled.span`
  color: ${colors.FC2_GREY};
  margin-right: 5px;
`
const Discount = styled.span`
  color: ${colors.FC2_RED};
`
const DetailValue = styled.p`
  margin: 0;
  color: ${colors.FC2_BLACK};
  ${fonts.SUBTITLE_3}
`
const PriceLine = styled.div`
  align-items: flex-end;
  color: ${colors.FC2_DARK_GREY};
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`
